import React, {
  KeyboardEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import IconButton from '@mui/material/IconButton';
import clsx from 'clsx';
import isNil from 'lodash/isNil';
import {
  Controller,
  useForm,
} from 'react-hook-form';
import Input from 'pages/Dashboard/components/Input';
import Spinner from 'pages/Dashboard/components/Spinner';
import TextArea from 'core/components/TextArea';
import ToolTip from 'core/components/Tooltip';
import DeleteIcon from 'core/icons/delete';
import {
  PinIcon,
} from 'core/icons';
import {
  formatDateTime,
} from 'utils/date';
import {
  usePersistedSticky,
  useStickyMeta,
  useStickyDraft,
  useStickyActions,
  useEditingStickyUniqueId,
  useFocusRequestedStickyId,
} from 'views/EMR/Sticky/stores/stickyNotesStore';
import {
  useConfirmDeleteSticky,
  useContainerFocus,
  useFlashVisible,
  useStickyEditingFocus,
} from 'views/EMR/Sticky/hooks';
import {
  ClientSticky,
  StickyDraft,
} from 'views/EMR/Sticky/types';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PauseIcon from '@mui/icons-material/Pause';
import defer from 'lodash/defer';
import {
  MultiRootEditor,
} from 'ckeditor5';
import {
  Button,
} from 'pages/Dashboard/components/Button';
import {
  useMixpanelTrack,
} from 'core/hooks';

type Props = {
  stickyId: number;
};

const defaultSticky = {
  title: '',
  content: '',
  isPinned: false,
};

function moveFocusToContent(editor: MultiRootEditor | null) {
  if (!isNil(editor)) {
    editor.model.change((writer) => {
      const root = editor.model.document.getRoot('content');
      if (!isNil(root)) {
        writer.setSelection(root, 'end');
      }
    });
    editor.editing.view.focus();
  }
}

function getDefaultValues(persisted: ClientSticky | null): StickyDraft {
  return !isNil(persisted)
    ? {
      title: persisted?.title ?? '',
      content: persisted?.content ?? '',
      isPinned: persisted?.isPinned ?? false,
    }
    : defaultSticky;
}

export default function StickyCard({ stickyId }: Props) {
  const persisted = usePersistedSticky(stickyId);
  const meta = useStickyMeta(stickyId);
  const draft = useStickyDraft(stickyId);
  const editingStickyUniqueId = useEditingStickyUniqueId();
  const focusRequestedStickyId = useFocusRequestedStickyId();
  const { hasRemoteUpdateWhileEditing } = meta ?? {};
  const mixPanelTrack = useMixpanelTrack();

  useEffect(() => {
    if (hasRemoteUpdateWhileEditing) {
      mixPanelTrack('StickyParallelEditing', { id: stickyId });
    }
  }, [hasRemoteUpdateWhileEditing]);

  const actions = useStickyActions();

  // Get the stable uniqueId from persisted
  const uniqueId = persisted?.uniqueId;

  // Global editing state (for sort deferral and visual styling)
  const isEditing = !isNil(uniqueId) && editingStickyUniqueId === uniqueId;

  // Local state for RHF reset guard (tracks any focus within container)
  // const [isInteracting, setIsInteracting] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const titleInputRef = useRef<HTMLInputElement>(null);
  const contentEditorRef = useRef<MultiRootEditor | null>(null);
  const isNew = stickyId < 0;

  const defaultValues = useMemo<StickyDraft>(
    () => getDefaultValues(persisted),
    [persisted],
  );

  const {
    control,
    reset,
    watch,
    trigger,
  } = useForm<StickyDraft>({
    defaultValues,
    mode: 'onChange',
  });

  const {
    handleInputFocus,
    handleInputBlur,
  } = useStickyEditingFocus(uniqueId, actions);

  const isPinned = watch('isPinned');

  useEffect(() => {
    if (isNew) {
      titleInputRef.current?.focus?.();
    }
  }, [isNew]);

  // Focus title when "Create Sticky" was clicked while this draft already existed (soft-lock)
  useEffect(() => {
    if (focusRequestedStickyId === stickyId) {
      titleInputRef.current?.focus?.();
      actions.clearFocusStickyRequest();
    }
  }, [focusRequestedStickyId, stickyId, actions]);

  // Move focus from title to content on Enter key press
  const handleTitleKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      moveFocusToContent(contentEditorRef.current);
    }
  }, []);

  const isInteracting = useContainerFocus({
    containerRef,
    onFocusOut: () => {
      defer(() => {
        trigger('title');
      });
    },
  });

  // Reset form when persisted changes (but not while interacting)
  useEffect(() => {
    if (!isNil(persisted)) {
      actions.prepareDraftForEditing(stickyId);

      // Don't reset while user is actively interacting
      if (!isInteracting) {
        const next = draft ?? defaultValues;
        reset(next, {
          keepDirty: false,
        });
      }
    }
  }, [stickyId, persisted?.updatedOn, isInteracting, draft, defaultValues, actions, reset]);

  const handleDelete = useConfirmDeleteSticky(stickyId, isNew);

  const handlePinToggle = useCallback(async (checked: boolean) => {
    await actions.togglePin(stickyId, checked);
  }, [stickyId, actions]);

  const flashSaved = useFlashVisible(meta?.savedFlashUntil);

  const containerClasses = clsx(
    'sticky-container flex flex-col justify-start border-gray-200 items-start gap-2 py-2 rounded-lg border bg-white',
    {
      'shadow-lg': isEditing,
      'pb-0': meta?.hasRemoteUpdateWhileEditing,
    },
  );

  const disableDelete = !isNew && isPinned;
  const deleteToolip = isNew ? 'Discard' : 'Delete';

  return !isNil(persisted) ? (
    <div className={containerClasses} ref={containerRef}>
      <div className="flex flex-col w-full pr-4 pl-2.5">
        <div className="flex justify-between items-center w-full gap-4">
          <Controller
            name="title"
            control={control}
            rules={{ required: true }}
            render={({ field, fieldState: { error } }) => (
              <Input
                {...field}
                error={!isNil(error)}
                showErrorText={false}
                value={field.value ?? ''}
                className="min-w-60 flex-1"
                inputClasses="text-sm transparent-input"
                placeholder="Enter title"
                id={`title-${stickyId}`}
                inputRef={titleInputRef}
                onFocus={handleInputFocus}
                onBlur={handleInputBlur}
                onKeyDown={handleTitleKeyDown}
                onChange={(val) => {
                  field.onChange(val);
                  actions.updateDraft(stickyId, { title: val });
                }}
                sx={{
                  '.MuiOutlinedInput-input': {
                    fontSize: 15,
                    fontWeight: 700,
                    color: 'black',
                  },
                }}
              />
            )}
          />
          <div className="flex items-center gap-px h-10">
            <Controller
              name="isPinned"
              control={control}
              render={({ field: { value: isPinned, onChange } }) => (
                <ToolTip title={isPinned ? 'Unpin' : 'Pin'}>
                  <IconButton
                    type="button"
                    aria-pressed={isPinned}
                    aria-label={isPinned ? 'Unpin note' : 'Pin note'}
                    onMouseDown={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                    }}
                    onClick={() => {
                      const next = !isPinned;
                      onChange(next);
                      handlePinToggle(next);
                    }}
                    className="!p-2"
                    color="info"
                  >
                    <PinIcon className={isPinned ? '!w-4 !h-4 !text-blue-500' : '!w-4 !h-4 !text-gray-600'} />
                  </IconButton>
                </ToolTip>
              )}
            />
            <ToolTip
              title={disableDelete ? 'Pinned stickies cannot be deleted—unpin to remove' : deleteToolip}
            >
              <span className={clsx({ 'cursor-not-allowed opacity-50': disableDelete })}>
                <IconButton
                  color="error"
                  aria-label="Delete Sticky"
                  disabled={disableDelete}
                  onClick={handleDelete}
                  className="!h-8 !w-8"
                >
                  <DeleteIcon className="!text-xl text-black -mt-1" />
                </IconButton>
              </span>
            </ToolTip>
          </div>
        </div>
        <Controller
          name="content"
          control={control}
          render={({ field }) => (
            <TextArea
              {...field}
              trackingLabel="Sticky Description"
              id={`content-${stickyId}`}
              floatingToolbar
              value={field.value ?? ''}
              classes={{
                root: 'sticky-textarea min-h-[40px]',
              }}
              onFocus={handleInputFocus}
              onBlur={handleInputBlur}
              onChange={(data) => {
                field.onChange(data);
                actions.updateDraft(stickyId, { content: data });
              }}
              onEditorReady={(editor) => {
                contentEditorRef.current = editor;
              }}
            />
          )}
        />
      </div>
      <div className="flex justify-between items-center w-full pr-4 pl-2.5">
        <div className="flex items-center gap-2">
          {!isNew && !isNil(persisted.updatedOn) && (
            <p className="text-xs text-gray-500 pl-2">
              Updated
              {' '}
              {formatDateTime(persisted.updatedOn)}
            </p>
          )}
        </div>
        <div className="min-w-[130px] flex-1 flex items-center justify-end !text-gray-500 h-5 mr-2">
          {(meta?.blocked === 'validation') ? (
            <div className="text-xs text-gray-500 font-medium">
              Fill in title to save
            </div>
          ) : (
            <>
              {!isNil(meta?.error) && (
                <ToolTip title={meta.error}>
                  <span className="flex items-center gap-1.5 text-red-600 animate-status-fade-in">
                    <span className="text-xs font-medium">Error saving</span>
                  </span>
                </ToolTip>
              )}
              {isNil(meta?.error) && meta?.saving && (
                <span className="flex items-center gap-1 animate-status-fade-in">
                  <Spinner className="!text-blue-600" size={14} />
                  <span className="text-xs font-medium">Saving...</span>
                </span>
              )}

              {/* Saved success flash */}
              {isNil(meta?.error) && !meta?.saving && flashSaved && (
                <span className="flex items-center gap-1 animate-status-scale-in">
                  <CheckCircleIcon className="!text-[16px]" />
                  <span className="text-xs font-medium">Saved</span>
                </span>
              )}
            </>
          )}
        </div>
      </div>
      {meta?.hasRemoteUpdateWhileEditing && (
        <div className="w-full text-xs flex justify-between items-center gap-2 border-t border-blue-100 bg-blue-50 pr-4 pl-2.5 py-2 -mb-2 rounded-b-lg">
          <span className="flex items-center gap-1 whitespace-nowrap text-gray-700 font-semibold">
            <PauseIcon className="!w-4 !h-4" />
            <span>Saving paused</span>
            <span>•</span>
            <span className="text-gray-500 font-medium">Edited elsewhere</span>
          </span>
          <div className="flex items-center gap-2">
            <Button
              variant="text"
              size="small"
              className="!text-gray-600 text-xs"
              color="secondary"
              onMouseDown={(e) => {
                e.stopPropagation();
                e.preventDefault();
              }}
              onClick={async () => {
                mixPanelTrack('StickyParallelEditingIgnored', { id: stickyId });
                await actions.ignoreRemoteUpdate(stickyId);
              }}
            >
              Ignore
            </Button>
            <Button
              variant="contained"
              size="small"
              onMouseDown={(e) => {
                e.stopPropagation();
                e.preventDefault();
              }}
              className="!bg-blue-500 hover:!bg-blue-600 !text-white !px-3 !py-1 !text-xs"
              onClick={() => {
                mixPanelTrack('StickyParallelEditingSynced', { id: stickyId });
                actions.syncWithRemote(stickyId);
                (document.activeElement as HTMLElement)?.blur?.();
              }}
            >
              Sync
            </Button>
          </div>
        </div>
      )}
    </div>
  ) : null;
}
